import { useMutation, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import { excerptsQueryKeys } from '@/hooks/excerpts/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { CreateExcerpt, Excerpt } from '@/types/library/Excerpt'

interface CreateExcerptArgs {
  orgId: UUID | ''
}

const createExcerpt = async (
  orgId: UUID | '',
  excerpt: CreateExcerpt,
  accessToken: string,
): Promise<Excerpt> => {
  if (!orgId) {
    throw new Error('No org ID provided.')
  }
  const body: CreateExcerpt = {
    content: excerpt.content,
    name: excerpt.name,
    tag_ids: excerpt.tag_ids,
    type: excerpt.type,
    pursuit_id: excerpt.pursuit_id,
    folder_id: excerpt.folder_id,
    markdown: excerpt.markdown,
    unrestricted: excerpt.unrestricted,
  }
  const res: Response = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/excerpts`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  }
  return (await res.json()) as Excerpt
}

export const useCreateExcerpt = ({ orgId }: CreateExcerptArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const { handleError } = useHandleError()

  const queryClient = useQueryClient()

  return useMutation<Excerpt, Error, CreateExcerpt, void>({
    mutationFn: (excerpt) => createExcerpt(orgId, excerpt, accessToken),
    onError: (err, excerpt, context) => {
      // rollback to the previous value
      // TODO: figure out how to properly handle this error, since it propagates to the user
      const key = excerptsQueryKeys.pursuitOrOrganization({
        pursuitId: excerpt.pursuit_id,
        orgId,
      })
      queryClient.setQueryData(key, context)
      handleError(err)
    },
    onSuccess: async (data, excerpt) => {
      // update the appropriate query cache
      // if it's a pursuit, update the pursuit query cache
      // if it's an org, update the org query cache
      const key = excerptsQueryKeys.pursuitOrOrganization({
        pursuitId: excerpt.pursuit_id,
        orgId,
      })
      queryClient.setQueryData<PaginatedResponse<Excerpt>>(key, (oldData) => {
        if (!oldData)
          return {
            data: [data],
            totalCount: 1,
            count: 1,
            has_more: false,
          }
        return {
          ...oldData,
          data: [data, ...oldData.data],
          count: oldData.count + 1,
          has_more: oldData.has_more,
        }
      })

      return queryClient.invalidateQueries({ queryKey: key })
    },
  })
}
